import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  AggregateBusinessApi,
  AggregateBusinessIdApi,
  AggregateBusinessIdResponse,
  AggregateBusinessIdResponseAllOfResult,
  AggregateBusinessMemoAggregateBusinessIdPutRequest,
  AggregateBusinessMemoApi,
  AggregateBusinessPostRequest,
  BusinessNameFromMasterApi,
  CheckDuplicateAggregateBusinessApi,
  ErrorResponse,
  ExistsAggregateBusinessAndBaseBusinessAllOfResult,
  ExistsAggregateBusinessAndBaseBusinessParameters,
  ExistsAggregateBusinessApi,
  ExistsAggregateBusinessParameters,
  ExistsAggregateBusinessSuccessAllOfResult,
  GetSimulatedAggregateBusinessInfo,
  GetSimulatedAggregateBusinessResponse,
  PostAggregateBusinessResponse,
  PostAggregateBusinessResponseResult,
  SimulateAggregateBusinessApi,
  SupplierNameFromMasterApi,
  WarehouseFromMasterApi,
  SuccessResponse,
  LeadTimeAggregateBusinessIdPutRequest,
  LeadTimeApi,
  StdInventoryIndicesAggregateBusinessIdPutRequest,
  StdInventoryIndicesApi,
  AggregateBusinessPutRequest,
  MoqAggregateBusinessIdPutRequest,
  MoqApi,
  OrderPackageAggregateBusinessIdPutRequest,
  OrderPackageApi,
  LastCheckedApi,
  PutLastCheckedResponseAllOfResult,
  GetAggregateBusinessSuccessResponseAllOfResult,
  AggregateBusinessGetUniqueKeysParameter,
  BulkPutAggregateBusinessRequestUpdateInfos,
  RecommendHiddenApi,
  AlertVisibilityApi,
} from '../axios/generated/aggregateBusinessManage';
import { IAggregateBusinessRepository } from '../interfaces/IAggregateBusinessRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { AxiosResponse } from 'axios';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { AggregateBusinessFilterProperty } from '@/coreDomainsModule/entities/filter';

/**
 * Axiosを使ったリポジトリ実装例
 */
export class AggregateBusinessRepository implements IAggregateBusinessRepository {
  /**
   * 集計をID or 必須キー集合 から取得する
   * なお、IDと必須キー集合は排他になっている
   * @param aggregateBusinessId - 集計ID
   * @param uniqueKeys - 必須キー集合
   */
  async get(
    aggregateBusinessId?: string,
    uniqueKeys?: AggregateBusinessGetUniqueKeysParameter
  ): Promise<INetworkResult<GetAggregateBusinessSuccessResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.aggregateBusinessGet(aggregateBusinessId, uniqueKeys);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
  /**
   * 集計情報の取得(詳細ページシミュレーション用)
   * @param aggregateBusinessId - 集計ID
   */
  async getSimulationBusiness(aggregateBusinessId: string): Promise<INetworkResult<GetSimulatedAggregateBusinessInfo, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new SimulateAggregateBusinessApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<GetSimulatedAggregateBusinessResponse, ErrorResponse>;
    try {
      apiResponse = await api.simulateAggregateBusinessAggregateBusinessIdGet(aggregateBusinessId);
      switch (apiResponse.status) {
        case 200:
          const result: GetSimulatedAggregateBusinessInfo = apiResponse.data.result.simulatedAggregateBusiness;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計IDの取得
   * @param aggregateBusinessFilterProperty - 集計情報のフィルタリング条件(全部空ではない想定)
   */
  async getAggregateBusinessId(aggregateBusinessFilterProperty: AggregateBusinessFilterProperty): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessIdApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<AggregateBusinessIdResponse, ErrorResponse>;
    try {
      apiResponse = await api.aggregateBusinessIdGet(
        aggregateBusinessFilterProperty.supplierName ?? '',
        aggregateBusinessFilterProperty.businessCode ?? '',
        aggregateBusinessFilterProperty.businessName ?? '',
        Number(aggregateBusinessFilterProperty.packageAmount) ?? 0,
        aggregateBusinessFilterProperty.warehouse ?? ''
      );
      switch (apiResponse.status) {
        case 200:
          const result: AggregateBusinessIdResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result.aggregateBusinessId } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計情報の登録
   * とりあえず、bulkは考えず単体の1取引分の情報をputする
   * @param aggregateBusinessInfo - 登録する集計情報
   */
  async post(aggregateBusinessInfo: AggregateBusinessPostRequest): Promise<INetworkResult<PostAggregateBusinessResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const aggregateBusinessApi = new AggregateBusinessApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<PostAggregateBusinessResponse, ErrorResponse>;

    try {
      apiResponse = await aggregateBusinessApi.aggregateBusinessPost(aggregateBusinessInfo);
      switch (apiResponse.status) {
        case 200:
          const result: PostAggregateBusinessResponseResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計情報の個別編集
   * @param aggregateBusinessInfo - 編集後の集計情報
   */
  async put(aggregateBusinessInfo: AggregateBusinessPutRequest): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse<SuccessResponse, IError> = await api.aggregateBusinessPut(aggregateBusinessInfo);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計情報の一括編集
   * @param targetIds - 対象のID
   * @param updateInfos - 更新情報
   * @param nullKeys - NULLにするキーリスト
   */
  async bulkPut(
    targetIds: string[],
    updateInfos: BulkPutAggregateBusinessRequestUpdateInfos,
    nullKeys?: string[]
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse: AxiosResponse<SuccessResponse, IError> = await api.aggregateBusinessBulkPut({ targetIds, updateInfos, nullKeys });
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計情報の重複確認
   * @param parameters - 存在確認用のパラメータ
   */
  async checkDuplicateAggregateBusiness(
    parameters: ExistsAggregateBusinessParameters
  ): Promise<INetworkResult<ExistsAggregateBusinessSuccessAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new ExistsAggregateBusinessApi(undefined, undefined, axiosInstance);
    // APIを叩く

    try {
      const apiResponse = await api.existsAggregateBusinessGet(parameters);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計情報と関連情報の重複確認
   * @param parameters - 重複確認用のパラメータ
   * @param limit - 取得する最大数
   */
  async checkDuplicateAggregateBusinessAndBusiness(
    parameters: ExistsAggregateBusinessAndBaseBusinessParameters,
    limit: number
  ): Promise<INetworkResult<ExistsAggregateBusinessAndBaseBusinessAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new CheckDuplicateAggregateBusinessApi(undefined, undefined, axiosInstance);
    // APIを叩く

    try {
      const apiResponse = await api.existsAggregateBusinessAndBaseBusinessGet(parameters, limit);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 商品名を取得
   * @param businessNumber - 商品CD
   */
  async getBusinessName(businessNumber: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BusinessNameFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.businessNameFromMasterGet(undefined, businessNumber);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.businessNames?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
  /**
   * 集計テーブル上のメモを追加/変更する
   * @param aggregateBusinessId - 追加/変更する集計ID
   * @param aggregateBusinessMemoAggregateBusinessIdPutRequest - リクエスト（メモの中身）
   */
  async putAggregateBusinessMemo(
    aggregateBusinessId: string,
    aggregateBusinessMemoAggregateBusinessIdPutRequest: AggregateBusinessMemoAggregateBusinessIdPutRequest
  ): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessMemoApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.aggregateBusinessMemoAggregateBusinessIdPut(
        aggregateBusinessId,
        aggregateBusinessMemoAggregateBusinessIdPutRequest
      );
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.code as unknown as string } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 商品CDを取得
   * @param businessName - 商品CD
   */
  async getBusinessNumber(businessName: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new BusinessNameFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.businessNameFromMasterGet(businessName);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.businessNumbers?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先名から仕入先番号を取得
   * @param supplierName - 仕入れきき名
   */
  async getSupplierNumber(supplierName: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new SupplierNameFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.supplierNameFromMasterGet(supplierName);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.supplierNumbers?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 仕入先番号から仕入先名を取得
   * @param supplierNumber - 仕入先番号
   */
  async getSupplierName(supplierNumber: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new SupplierNameFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.supplierNameFromMasterGet(undefined, supplierNumber);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.supplierNames?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 倉庫名から倉庫番号を取得
   * @param warehouseName - 倉庫名
   */
  async getWarehouseNumber(warehouseName: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new WarehouseFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.warehouseFromMasterGet(warehouseName);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.warehouseNumbers?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 倉庫番号から倉庫名を取得
   * @param warehouseNumber - 倉庫番号
   */
  async getWarehouseName(warehouseNumber: string): Promise<INetworkResult<string | undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new WarehouseFromMasterApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await api.warehouseFromMasterGet(undefined, warehouseNumber);
      switch (apiResponse.status) {
        case 200:
          const result: string | undefined = apiResponse.data.result.warehouses?.[0];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
  /**
   * 集計テーブル上のメモを削除する
   * @param aggregateBusinessId - 追加/変更する集計ID
   */
  async deleteAggregateBusinessMemo(aggregateBusinessId: string): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AggregateBusinessMemoApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.aggregateBusinessMemoAggregateBusinessIdDelete(aggregateBusinessId);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.code as unknown as string } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * リードタイムの更新
   * @param aggregateBusinessId - 集計ID
   * @param leadTimeAggregateBusinessIdPutRequest - リードタイムのリクエスト
   */
  async putLeadTime(
    aggregateBusinessId: string,
    leadTimeAggregateBusinessIdPutRequest: LeadTimeAggregateBusinessIdPutRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new LeadTimeApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.leadTimeAggregateBusinessIdPut(aggregateBusinessId, leadTimeAggregateBusinessIdPutRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 標準在庫指数の更新
   * @param aggregateBusinessId - 集計ID
   * @param stdInventoryIndicesAggregateBusinessIdPutRequest - 標準在庫指数のリクエスト
   */
  async putStdInventoryIndices(
    aggregateBusinessId: string,
    stdInventoryIndicesAggregateBusinessIdPutRequest: StdInventoryIndicesAggregateBusinessIdPutRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new StdInventoryIndicesApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await api.stdInventoryIndicesAggregateBusinessIdPut(
        aggregateBusinessId,
        stdInventoryIndicesAggregateBusinessIdPutRequest
      );
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * MOQの更新
   * @param aggregateBusinessId - 集計取引ID
   * @param moqAggregateBusinessIdPutRequest - MOQのリクエスト
   */
  async putMoq(
    aggregateBusinessId: string,
    moqAggregateBusinessIdPutRequest: MoqAggregateBusinessIdPutRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new MoqApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.moqAggregateBusinessIdPut(aggregateBusinessId, moqAggregateBusinessIdPutRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注単位の更新
   * @param aggregateBusinessId - 集計取引ID
   * @param orderPackageAggregateBusinessIdPutRequest - 発注単位のリクエスト
   */
  async putOrderPackage(
    aggregateBusinessId: string,
    orderPackageAggregateBusinessIdPutRequest: OrderPackageAggregateBusinessIdPutRequest
  ): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new OrderPackageApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.orderPackageAggregateBusinessIdPut(aggregateBusinessId, orderPackageAggregateBusinessIdPutRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 最終チェック情報の更新
   * @param aggregateBusinessId - 集計取引ID
   */
  async putLastChecked(aggregateBusinessId: string): Promise<INetworkResult<PutLastCheckedResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new LastCheckedApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.lastCheckedAggregateBusinessIdPut(aggregateBusinessId);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 推奨を一時的に非表示にする
   * @param aggregateBusinessIds - 集計IDリスト
   * @param isHidden - 非表示にするかどうか
   */
  async putRecommendHidden(aggregateBusinessIds: string[], isHidden: boolean): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new RecommendHiddenApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.aggregateBusinessRecommendHiddenPut({ aggregateBusinessIds, isHidden });
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 集計のアラート表示設定を変更する
   * @param aggregateBusinessIds - 集計IDリスト
   * @param isDismissed - 非表示にするかどうか
   */
  async putAlertVisibility(aggregateBusinessIds: string[], isDismissed: boolean): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const api = new AlertVisibilityApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await api.aggregateBusinessAlertVisibilityPut({ aggregateBusinessIds, isDismissed });
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('Unexpected response');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}

/* APIの疎通確認 */
/**
 * 登録用のサンプルオブジェクト
 * このまま入れると2個目以上入らないので要改良
 */
const requestSample: AggregateBusinessPostRequest = {
  postAggregateBusinessInfo: {
    supplierName: '株式会社テストC',
    businessNumber: 'BN20240518',
    businessName: 'テスト取引',
    package: '500',
    warehouse: '東京倉庫',
    unit: 'kg',
    stdInventoryIndicesMin: '100.0',
    stdInventoryIndicesMax: '500.0',
    stdInventoryIndicesUnit: 'pieces',
    leadTime: '30',
    leadTimeUnit: 'days',
    moq: '10.0',
    executionDate: '1999-12-31 00:00:00Z',
    aggregateBusinessNote: '特記事項',
    supplierNumber: 'sampleSN-1',
    warehouseNumber: 'sampleWN-1',
    aggregateBusinessResponsibleUserIdList: [],
  },
};

/**
 * aggregateBusinessのサンプルデータ作成を行う
 */
const createRequestSample = (): AggregateBusinessPostRequest => {
  return {
    postAggregateBusinessInfo: {
      supplierName: `株式会社テストC_${new Date()}`,
      businessNumber: 'BN20240518',
      businessName: 'テスト取引',
      package: '500',
      warehouse: '東京倉庫',
      unit: 'kg',
      stdInventoryIndicesMin: '100.0',
      stdInventoryIndicesMax: '500.0',
      stdInventoryIndicesUnit: 'pieces',
      leadTime: '30',
      leadTimeUnit: 'days',
      moq: '10.0',
      executionDate: '1999-12-31 00:00:00Z',
      aggregateBusinessNote: '特記事項',
      supplierNumber: 'sampleSN-1',
      warehouseNumber: 'sampleWN-1',
      aggregateBusinessResponsibleUserIdList: [],
    },
  };
};

/**
 * aggregateBusinessのサンプルデータ作成を行う関数
 * index渡して、その数だけ生成する
 * @param count - 生成する数
 */
const createBulkRequestSample = (count: number) => {
  // TODO: implement
  const output = [];
  for (let i = 0; i < count; i++) {
    output.push();
  }
};

/**
 * API確認用の関数
 * あくまで確認する用で簡易化してます
 * @param aggregateBusinessRepository - リポジトリのインスタンス
 */
export const checkPutAggregateBusinessRepositoryApi = async (aggregateBusinessRepository: AggregateBusinessRepository) => {
  const res = await aggregateBusinessRepository.post(createRequestSample()); // 設置されているサンプルを挿入
  console.log({ レスポンスチェック: res });
};
